import { mapsLocalization } from '../translations';

/**
 * Wrapper class for Google Maps API
 * Currently only handles the Embed API b/c it's free unlimited usage.
 * See: https://developers.google.com/maps/documentation/javascript/tutorial
 */
export class GoogleMap {
  constructor(options = {}) {
    const {
      apiKey = '',
      el = undefined,
      embedMode = 'place',
      height = '400px',
      mapType = 'roadmap',
      center = {
        lat: '',
        lng: '',
      },
      markers = [],
      region = 'CA',
      width = '100%',
      zoom = 13,
    } = options;

    if (!apiKey) {
      throw new Error(
        mapsLocalization.gettext('Google Maps API Key is required to use this class'),
      );
    }

    this.apiKey = apiKey;
    this.el = el;
    this.embedMode = embedMode;
    this.height = height;
    this.mapType = mapType;
    this.markers = markers;
    this.region = region;
    this.width = width;
    this.zoom = zoom;
    this.center = center;
    this.iframe = document.createElement('iframe');

    this.initEmbedMap();
  }

  initEmbedMap() {
    const mapUrl = this.getEmbedMapUrl();
    this.appendEmbedMap(mapUrl);
  }

  /**
   * Creates the complete embed URL (including query string)
   * @returns {string}
   */
  getEmbedMapUrl() {
    const mapUrl = new URL('https://www.google.com/maps/embed/v1/' + this.embedMode);
    mapUrl.searchParams.set('key', this.apiKey);

    if (this.embedMode === 'place') {
      const place = this.getEmbedPlace();
      mapUrl.searchParams.set('q', place);
    }

    mapUrl.searchParams.set('zoom', this.zoom);
    mapUrl.searchParams.set('maptype', this.mapType);
    mapUrl.searchParams.set('region', this.region);

    return mapUrl.toString();
  }

  /**
   * Creates and appends a Google Map iFrame to the provided DOM element
   * @param url The embed url. See https://developers.google.com/maps/documentation/embed/guide
   */
  appendEmbedMap(url) {
    this.iframe.setAttribute('src', url);
    this.iframe.setAttribute('frameborder', '0');
    this.iframe.setAttribute('height', this.height);
    this.iframe.setAttribute('width', this.width);
    this.iframe.setAttribute('aria-label', mapsLocalization.gettext('Google Map'));

    this.el.appendChild(this.iframe);
  }

  setZoom(zoom) {
    this.zoom = zoom;
    this.updateMap();
  }

  setMapType(type) {
    this.mapType = type;
    this.updateMap();
  }

  togglePlace() {
    this.embedMode = this.embedMode === 'place' ? 'view' : 'place';
  }

  setEmbedMarker(marker = {}) {
    this.markers = [marker];
    this.updateMap();
  }

  setHeight(height) {
    this.height = height;
    this.updateMap();
  }

  setWidth(width) {
    this.width = width;
    this.updateMap();
  }

  updateMap() {
    this.iframe.setAttribute('src', this.getEmbedMapUrl());
    this.iframe.setAttribute('height', this.height);
    this.iframe.setAttribute('width', this.width);
  }

  /**
   * Get the embed place (q=embed_place)
   * Returns either address, title, or lat + lng (in that order)
   * @returns {*|string}
   */
  getEmbedPlace() {
    const marker = this.markers[0];

    if (marker.title && marker.address) {
      return marker.title + ' ' + marker.address;
    }

    return marker.address || marker.title || [marker.lat, marker.lng].join(',');
  }
}
